import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import { activePlayer } from "@/entities/audio-engine"
import { processSocket } from "@/entities/process-socket/store"

interface WordHit {
	start_ts: number
	end_ts: number
}

interface UseMarkdownAudioPageResult {
	/** 0-based page the audio is currently on, -1 while unknown */
	audioPage: number
	/** Call when tracker finds a word on a different page (avoids re-scan) */
	setAudioPage: (pageIndex: number) => void
	/** Trigger a full scan for the given position (big seek, no words found) */
	searchForPosition: (position: number) => void
	/** True while initial/seek scan is running */
	searching: boolean
}

/**
 * Which markdown page is the audio on? Ported from the PDF viewer's
 * useAudioPage, adapted to 0-based markdown pages.
 *
 * PROCESSING mode: socket-driven (poll every 500ms).
 * COMPLETED mode: spiral probing of per-page combined JSONs from an estimated
 * page outward — each probe is cached and doubles as a warm-up for the word
 * window.
 */
export const useMarkdownAudioPage = (
	contentId: string,
	totalPages: number,
	combinedUrls?: string[]
): UseMarkdownAudioPageResult => {
	const [audioPage, setAudioPageState] = useState(-1)
	const [searching, setSearching] = useState(false)
	const lastCheckedPosition = useRef<number | null>(null)
	const combinedCacheRef = useRef<Map<number, WordHit[]>>(new Map())
	const searchingRef = useRef(false)

	const isCompleted = !!combinedUrls?.length

	const setAudioPage = useCallback(
		(pageIndex: number) => {
			const clamped = Math.max(
				0,
				Math.min(totalPages > 0 ? totalPages - 1 : pageIndex, pageIndex)
			)
			setAudioPageState(prev => (prev === clamped ? prev : clamped))
		},
		[totalPages]
	)

	// ─── COMPLETED: scan combined URLs to find page ──────────

	const searchForPosition = useCallback(
		async (position: number) => {
			if (!combinedUrls?.length || totalPages <= 0 || searchingRef.current)
				return
			searchingRef.current = true
			setSearching(true)

			try {
				const downloadPage = async (
					pageIndex: number
				): Promise<WordHit[] | null> => {
					const cached = combinedCacheRef.current.get(pageIndex)
					if (cached) return cached
					const url = combinedUrls[pageIndex]
					if (!url) return null
					try {
						const res = await axios.get(url, {
							headers: {
								"Cache-Control": "public, max-age=31536000",
							},
						})
						const words: WordHit[] = (
							res.data as { start_ts: number; end_ts: number }[]
						).map(w => ({ start_ts: w.start_ts, end_ts: w.end_ts }))
						combinedCacheRef.current.set(pageIndex, words)
						return words
					} catch {
						return null
					}
				}

				const hasWordAtTime = (
					words: WordHit[],
					time: number
				): boolean => {
					let left = 0
					let right = words.length - 1
					while (left <= right) {
						const mid = Math.floor((left + right) / 2)
						const w = words[mid]
						if (time >= w.start_ts && time <= w.end_ts) return true
						if (time < w.start_ts) right = mid - 1
						else left = mid + 1
					}
					return false
				}

				// Check cache first
				for (const [pageIndex, words] of combinedCacheRef.current) {
					if (hasWordAtTime(words, position)) {
						setAudioPage(pageIndex)
						return
					}
				}

				// Estimate, then spiral outward
				const { totalDuration } = activePlayer
				const estimate =
					totalDuration > 0
						? Math.max(
								0,
								Math.min(
									totalPages - 1,
									Math.floor(
										(position / totalDuration) * totalPages
									)
								)
							)
						: 0

				for (let offset = 0; offset <= totalPages; offset++) {
					const candidates =
						offset === 0
							? [estimate]
							: [estimate - offset, estimate + offset]

					for (const pageIndex of candidates) {
						if (pageIndex < 0 || pageIndex > totalPages - 1) continue
						if (combinedCacheRef.current.has(pageIndex)) continue

						const words = await downloadPage(pageIndex)
						if (!words) continue
						if (hasWordAtTime(words, position)) {
							setAudioPage(pageIndex)
							return
						}
					}
				}

				// Fallback: closest page before position
				let bestPage = estimate
				let bestTs = -1
				for (const [pageIndex, words] of combinedCacheRef.current) {
					if (words.length === 0) continue
					const lastWord = words[words.length - 1]
					if (lastWord.end_ts <= position && lastWord.end_ts > bestTs) {
						bestTs = lastWord.end_ts
						bestPage = pageIndex
					}
				}
				setAudioPage(bestPage)
			} finally {
				searchingRef.current = false
				setSearching(false)
			}
		},
		[combinedUrls, totalPages, setAudioPage]
	)

	// ─── COMPLETED: one-time initial scan ────────────────────

	const hasInitializedRef = useRef(false)

	useEffect(() => {
		if (!isCompleted || totalPages <= 0) return
		if (hasInitializedRef.current) return

		let timer: ReturnType<typeof setTimeout> | null = null

		const init = async () => {
			const { currentPosition, totalDuration, isLoading } = activePlayer
			if (isLoading || totalDuration <= 0) {
				timer = setTimeout(init, 200)
				return
			}

			hasInitializedRef.current = true

			if (currentPosition > 0) {
				await searchForPosition(currentPosition)
			} else {
				// Position 0 = start of audio, page 0 is correct
				setAudioPage(0)
			}
		}

		init()

		return () => {
			if (timer) clearTimeout(timer)
		}
	}, [isCompleted, totalPages, searchForPosition, setAudioPage])

	// ─── PROCESSING: socket polling ──────────────────────────

	useEffect(() => {
		if (isCompleted) return
		if (totalPages <= 0 || !contentId) return

		const handleResponse = (data: { pageIndex: number }) => {
			if (!data || typeof data.pageIndex !== "number") return
			// -1 is the server's "not aligned yet" sentinel for markdown
			if (data.pageIndex < 0) return
			setAudioPage(data.pageIndex)
		}

		processSocket.on("page-for-audio-time", handleResponse)

		const check = () => {
			const { currentPosition, totalDuration, isLoading } = activePlayer
			if (isLoading || totalDuration <= 0 || currentPosition <= 0) return
			if (!processSocket.connected) return

			if (
				lastCheckedPosition.current !== null &&
				Math.abs(currentPosition - lastCheckedPosition.current) < 0.5
			) {
				return
			}

			lastCheckedPosition.current = currentPosition

			processSocket.emit("get-page-for-audio-time", {
				contentId,
				audioTime: currentPosition,
			})
		}

		check()
		const interval = setInterval(check, 500)

		return () => {
			processSocket.off("page-for-audio-time", handleResponse)
			clearInterval(interval)
		}
	}, [isCompleted, contentId, totalPages, setAudioPage])

	return { audioPage, setAudioPage, searchForPosition, searching }
}
